/**
 * MarkupCanvas — the drawing surface over the photo while the editor is open (gallery F-6).
 * Rendered in the viewer's overlay slot, so it sits on the photo and carries its rotate/zoom.
 *
 * Every pointer position is mapped through svg.getScreenCTM().inverse(), which undoes the
 * viewer's CSS rotate and zoom, so a mark lands exactly under the finger or cursor at any view.
 * `touch-action: none` stops the browser scrolling or zooming the page mid-stroke; the viewer's
 * own swipe and pinch are switched off by its `editing` mode.
 */
import { useCallback, useRef, type Dispatch } from 'react';
import type { MarkupPoint } from '../../../../types/fieldService/generatorPhotoMarkupVocab';
import type { MarkupAction, MarkupEditorState } from './markupEditorState';
import { screenToNormalised } from './markupGeometry';
import { MarkupShapes } from './MarkupShapes';
import { useBoxSize } from './useBoxSize';

interface MarkupCanvasProps {
  state: MarkupEditorState;
  dispatch: Dispatch<MarkupAction>;
}

export function MarkupCanvas({ state, dispatch }: MarkupCanvasProps) {
  const ref = useRef<SVGSVGElement>(null);
  const { w, h } = useBoxSize(ref);

  const toPoint = useCallback(
    (e: React.PointerEvent): MarkupPoint | null => {
      const ctm = ref.current?.getScreenCTM?.();
      if (!ctm) return null;
      return screenToNormalised(e.clientX, e.clientY, ctm.inverse(), w, h);
    },
    [w, h]
  );

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const point = toPoint(e);
    if (!point) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dispatch({ type: 'pointerDown', point });
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!state.anchor) return;
    const point = toPoint(e);
    if (point) dispatch({ type: 'pointerMove', point });
  };

  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    dispatch({ type: 'pointerUp' });
  };

  const onShapePointerDown = (id: string, e: React.PointerEvent<SVGElement>) => {
    if (state.tool !== 'select') return;
    const point = toPoint(e);
    if (!point) return;
    e.stopPropagation();
    e.preventDefault();
    ref.current?.setPointerCapture?.(e.pointerId);
    dispatch({ type: 'pickShape', id, point });
  };

  const shapes = state.draft ? [...state.shapes, state.draft] : state.shapes;
  const cursor = state.tool === 'select' ? 'default' : state.tool === 'text' ? 'text' : 'crosshair';

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${w} ${h}`}
      className="absolute inset-0 h-full w-full"
      style={{ touchAction: 'none', cursor }}
      data-testid="markup-canvas"
      aria-hidden="true"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* Transparent hit area: pointer events anywhere on the photo, not only on marks. */}
      <rect x={0} y={0} width={w} height={h} fill="transparent" />
      <MarkupShapes
        shapes={shapes}
        w={w}
        h={h}
        selectedId={state.selectedId}
        onShapePointerDown={state.tool === 'select' ? onShapePointerDown : undefined}
      />
    </svg>
  );
}
